import { Icon16Cancel, Icon16Done, Icon20SmileAddOutline, Icon28Hearts2Outline } from '@vkontakte/icons';
import theme from '@vkontakte/vkui-tokens/themes/cloud/cssVars/theme';
import classNames from 'clsx';
import { ReactComponent as IconUnlimitedAutoupload } from 'img/icons/unlimited-autoupload-16.svg';
import React, { type VFC } from 'react';
import { beautifulAddressEnabled } from 'reactApp/appHelpers/featuresHelpers/features/beautifulAddress';
import { compactFirstScroll } from 'reactApp/appHelpers/featuresHelpers/features/compactFirstScroll';
import { enabledFamilyTariff } from 'reactApp/appHelpers/featuresHelpers/features/familyTariff';
import { multiBasicWithoutBaz, multiTariffShow } from 'reactApp/appHelpers/featuresHelpers/features/multiTariff';
import {
    showEditingDocumentFeatureB,
    showEditingDocumentFeatureC,
} from 'reactApp/appHelpers/featuresHelpers/features/showEditingDocumentFeature';
import { FeaturesColorTypes } from 'reactApp/constants/products/features.types';
import { TariffCardContentSize } from 'reactApp/ui/TariffCardNew/TariffCard.types';

import styles from './TariffFeatureList.css';
import type { ITariffFeatureListProps } from './TariffFeatureList.types';

export const TariffFeatureList: VFC<ITariffFeatureListProps> = ({
    dataQa,
    items,
    showIcon = false,
    large = false,
    accent = false,
    contentSize = TariffCardContentSize.middle,
    isRebrandingQuotaLanding = false,
    featuresSmallSize,
    isRebranding = false,
    forceFamilyTariff = false,
    isMultiBasic = false,
    isMultiAdvanced = false,
    isVigboTariff = false,
    isFamilyTariff = false,
    blackFriday2025Enabled,
}) => (
    <ul
        className={classNames(styles.featureList, {
            [styles[`contentSize_${contentSize}`]]: !!contentSize,
            [styles.featureList_rebrandingQuotaLanding]: isRebrandingQuotaLanding,
            [styles.featureList_smallSize]: featuresSmallSize,
            [styles.featureList_compactScroll]: compactFirstScroll && isRebrandingQuotaLanding,
            [styles.featureList_showEditingDocumentFeatureB]: showEditingDocumentFeatureB,
            [styles.featureList_vigbo]: isVigboTariff,
        })}
        data-qa={dataQa}
    >
        {items.map((item) => {
            const isLime =
                item?.color === FeaturesColorTypes.lime ||
                (!isFamilyTariff &&
                    !item.forcePrimaryColor &&
                    ((item.key === 'unlimit-autoupload' && !item.disabled) ||
                        ((enabledFamilyTariff || forceFamilyTariff) &&
                            (item.key === 'family-folders-albums' || item.key === 'family-tariff'))));

            const limeIconColor = isLime ? 'var(--vkui--color_accent_lime)' : 'var(--vkui--color_icon_primary)';

            return (
                <li
                    key={item.key}
                    style={{ color: item.styleColor }}
                    className={classNames(styles.featureListItem, {
                        [styles.featureListItem_large]: large,
                        [styles.featureListItem_accentUrl]: accent,
                        [styles.featureListItem_accent]: item.accent,
                        [styles.featureListItem_pointer]: !!item.onClick,
                        [styles.featureListItem_rebranding]: isRebranding,
                        [styles.featureListItem_bright]: showEditingDocumentFeatureB || showEditingDocumentFeatureC,
                        [styles.featureListItem_disabled]: item.disabled,
                        [styles.featureListItem_lime]: isLime && !isVigboTariff,
                        [styles.featureListItem_primaryColor]: item.primaryColor,
                        [styles.featureListItem_disabled_beauty]:
                            item.disabled && ((beautifulAddressEnabled && multiTariffShow) || (multiBasicWithoutBaz && isMultiBasic)),
                        [styles.featureListItem_disabledNew]: item.disabled && !multiBasicWithoutBaz,
                        [styles.featureListItem_blackFriday2025]: blackFriday2025Enabled && isMultiAdvanced && isLime,
                    })}
                    onClick={item.onClick}
                >
                    {showIcon && !item.noIcon && (
                        <div className={styles.featureListItemIcon}>
                            {item.icon && item.usePropsIcon ? (
                                item.icon
                            ) : item.icon || item.disabled ? (
                                <Icon16Cancel />
                            ) : item.key === 'unlimit-autoupload' ? (
                                <IconUnlimitedAutoupload fill={limeIconColor} stroke={limeIconColor} />
                            ) : item.key === 'family-tariff' ? (
                                <Icon20SmileAddOutline fill={limeIconColor} width={16} height={16} />
                            ) : item.key === 'family-folders-albums' ? (
                                <Icon28Hearts2Outline fill={limeIconColor} width={16} height={16} />
                            ) : (
                                <Icon16Done
                                    fill={
                                        item.primaryColor
                                            ? theme.colorIconPrimary.normal.value
                                            : isRebrandingQuotaLanding
                                            ? theme.colorIconSecondary.normal.value
                                            : theme.colorIconAccent.normal.value
                                    }
                                />
                            )}
                        </div>
                    )}
                    <div data-qa={`${dataQa}_text`}>{item.text}</div>
                </li>
            );
        })}
    </ul>
);
